import {
  marketHistoryWindow,
  variantMarketHistory,
} from "@/components/pricing/history";
import React, { useMemo } from "react";
import { CardDetailedModalImage } from "./CardDetailedModalImage";
import { CardDetailsModalCardInfo } from "./CardDetailsModalCardInfo";
import CardDetailsModalPrice from "./CardDetailsModalPrice";
import { CardDetailsModalStats } from "./CardDetailsModalStats";
import CardDetailsModalPriceSkeleton from "./CardDetailsModalPriceSkeleton";
import CardDetailsModalStatsSkeleton from "./CardDetailsModalStatsSkeleton";
import { Tables } from "database.types";
import { getCardImageUrl } from "@/utils/getCardImageUrl";
import useVariantsCards from "@/hooks/useVariantsCards";
import { sortVariantsByConditions } from "@/utils/sortVariantsByConditions";
import CardDetailsModalVariants from "./CardDetailsModalVariants";
import CardDetailsModalVariantsSkeleton from "./CardDetailsModalVariantsSkeleton";
import { Radar, ShoppingCart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PriceAlertCard } from "../PriceAlertCard";
import { ResponsiveModal } from "@/components/ResponsiveModal/ResponsiveModal";
import BinderValueChart from "@/components/binder/BinderValueChart";
import { createTcgPlayeraAffiliateLink } from "@/utils/createTcgPlayeraAffiliateLink";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { CardDetailsModalCollection } from "./CardDetailsModalCollection";
import { TruePricePanel } from "@/components/pricing/TruePricePanel";

interface CardDetailsModalProps {
  card:
    | (Tables<"cards"> & { set: Tables<"sets">; game: Tables<"games"> })
    | null;
  isOpen: boolean;
  onClose: () => void;
  showBuyButton?: boolean;
  mobileAccentContent?: React.ReactNode;
  desktopAccentContent?: React.ReactNode;
  scanned?: boolean;
}

const CardDetailsModal: React.FC<CardDetailsModalProps> = ({
  card,
  isOpen,
  onClose,
  showBuyButton = true,
  mobileAccentContent,
  desktopAccentContent,
  scanned,
}) => {
  const { session } = useAuth();

  const isMobile = useIsMobile();

  const {
    data: variants,
    refetch,
    isFetching: isFetchingVariants,
  } = useVariantsCards({
    cardId: card?.id ?? 0,
    enabled: isOpen,
  });

  const sortedVariants = useMemo(() => {
    if (!variants) {
      return null;
    }
    return sortVariantsByConditions(variants);
  }, [variants]);

  const bestVariant = useMemo(() => {
    if (!sortedVariants) {
      return null;
    }
    const bestCardVariant = sortedVariants?.find(
      (variant) => variant.id === card?.best_variant_id,
    );
    return bestCardVariant || sortedVariants[0];
  }, [sortedVariants, card]);

  const price = useMemo(() => {
    const priceValue = card?.main_price ?? bestVariant?.price;

    if (!priceValue) {
      return null;
    }

    return Number(priceValue).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  }, [card, bestVariant]);

  const buyLink = useMemo(() => {
    if (!card) {
      return null;
    }

    if (!card.tcgplayer_id) {
      return null;
    }

    return createTcgPlayeraAffiliateLink(card.tcgplayer_id);
  }, [card]);

  const compareListingsLink = useMemo(() => {
    if (!card) return "/deal-hunter";

    const params = new URLSearchParams({
      cardId: String(card.id),
      cardFormat: "raw",
      language: (card as any).language ?? "english",
      condition: (bestVariant?.condition?.name ?? "any").replace(/([a-z])([A-Z])/g, "$1_$2").replace(/[ -]/g, "_").toLowerCase(),
      printing: (bestVariant?.printing?.name ?? "any").replace(/([a-z])([A-Z])/g, "$1_$2").replace(/[ -]/g, "_").toLowerCase(),
      minimumDiscountPercent: "10",
      autoSearch: "1",
      source: "card-details",
    });

    return `/deal-hunter?${params.toString()}`;
  }, [card, bestVariant]);

  if (!card) return null;

  const getCardImage = (): string => {
    return getCardImageUrl(card);
  };

  const getCardName = (): string => {
    return card.name || "";
  };

  const cardImageUrl = getCardImage();
  const cardName = getCardName();

  const isDisabledVariants = isFetchingVariants;

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={onClose}
      title={cardName}
      size="lg"
    >
      <div className="grid gap-4">
        {mobileAccentContent && isMobile && <div>{mobileAccentContent}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="grid gap-4">
            <CardDetailedModalImage imageUrl={cardImageUrl} alt={cardName} />
            {session && card && (
              <CardDetailsModalCollection
                card={card}
                variants={sortedVariants}
              />
            )}
            <CardDetailsModalCardInfo card={card} />
          </div>

          <div className="space-y-4">
            {desktopAccentContent && !isMobile && (
              <div>{desktopAccentContent}</div>
            )}
            {!bestVariant && <CardDetailsModalPriceSkeleton />}
            {bestVariant && (
              <CardDetailsModalPrice
                price={price ?? ""}
                priceChange24hr={
                  marketHistoryWindow(variantMarketHistory(bestVariant.raw), 1)
                    ?.changePercent ?? null
                }
              />
            )}
            {/* Price Statistics */}
            {!bestVariant && <CardDetailsModalStatsSkeleton />}
            {bestVariant && <CardDetailsModalStats variant={bestVariant} />}
            <TruePricePanel
              cardId={card.id}
              language={(card as any).language ?? "english"}
              condition={bestVariant?.condition?.name ?? undefined}
              printing={(bestVariant as any)?.printing?.name ?? undefined}
              compact
            />
            {session && <PriceAlertCard cardId={card.id} />}
            {showBuyButton && (
              <div className="grid gap-2 sm:grid-cols-2">
                <Link
                  to={compareListingsLink}
                  className="inline-flex w-full items-center justify-center rounded-md bg-electric-yellow p-3 font-medium text-deep-indigo hover:bg-electric-yellow/90"
                >
                  <Radar className="mr-2 h-4 w-4" />
                  Compare live listings
                </Link>
                {buyLink && (
                  <Link
                    to={buyLink}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="inline-flex w-full items-center justify-center rounded-md border border-input bg-background p-3 font-medium hover:bg-accent hover:text-accent-foreground"
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Buy on TCGplayer
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
        {sortedVariants && sortedVariants.length && bestVariant && (
          <BinderValueChart variants={[bestVariant]} />
        )}
        {/* All Variants - Full Width */}
        {!sortedVariants && <CardDetailsModalVariantsSkeleton />}
        {sortedVariants && (
          <CardDetailsModalVariants
            card={card}
            variants={sortedVariants}
            scanning={scanned}
          />
        )}
      </div>
    </ResponsiveModal>
  );
};

export default CardDetailsModal;
